import * as R from 'remeda';
import { createSelector } from 'reselect';
import { activitySelectors } from 'store/data/activity';
import { subZoneSelectors } from 'store/data/subZone';
import { zoneSelectors } from 'store/data/zone';
import Types from 'Types';
import {
  aggregationUtility,
  kpiUtility,
  targetNewUtility,
  timeUtility,
} from 'utility';

import { onRelevantItems } from '../helpers';

export const domain = (state: Types.RootState) => state.aggregated.internalHour;

export const byId = createSelector(domain, (domain) => domain.byId);
export const allIds = createSelector(domain, (domain) => domain.allIds);

export const item = createSelector(
  (state: Types.RootState, ownProps: { id: string }) => ownProps.id,
  byId,
  (id, byId) => R.pipe(byId, R.prop(id)),
);

export const list = createSelector(allIds, byId, (allIds, byId) =>
  R.pipe(
    allIds,
    R.map((id) => R.pipe(byId, R.prop(id))),
    R.filter(R.isDefined),
  ),
);

export const byDate = createSelector(list, (list) =>
  aggregationUtility.groupByDate(list),
);

export const allDates = createSelector(byDate, (byDate) => R.keys(byDate));

const byZone = createSelector(list, (list) =>
  aggregationUtility.groupBy(list, 'zoneId'),
);

const bySubZone = createSelector(list, (list) =>
  aggregationUtility.groupBy(list, 'subZoneId'),
);

const byActivity = createSelector(
  bySubZone,
  activitySelectors.list,
  (bySubZone, activities) =>
    R.pipe(
      activities,
      R.map((activity) => {
        if (!R.isDefined(activity.subZoneIds)) return;

        const internalIds = R.pipe(
          activity.subZoneIds,
          R.map((subZoneId) => bySubZone[subZoneId]),
          R.filter(R.isDefined),
          R.flatten(),
          R.uniq(),
        );

        return { id: activity.id, internalIds };
      }),
      R.filter(R.isDefined),
      R.mapToObj((a) => [String(a.id), a.internalIds]),
    ),
);

export const byEntity = createSelector(
  byZone,
  bySubZone,
  byActivity,
  (byZone, bySubZone, byActivity) => ({
    ...byZone,
    ...bySubZone,
    ...byActivity,
  }),
);

export const kpi = createSelector(
  (
    state: Types.RootState,
    ownProps: {
      entityIds: string[];
      dateId: string;
    },
  ) => ownProps,
  byDate,
  byEntity,
  byId,

  ({ dateId, entityIds }, byDate, byEntity, byId) => {
    let noProcessed: number | null = null;

    onRelevantItems({
      byId,
      byDate,
      byEntity,
      dateId,
      itemCallback: (item) => {
        noProcessed = kpiUtility.addNullableValue(
          noProcessed,
          item.noProcessed,
        );
      },
      entityIds,
    });

    const kpiValues: KPI.InternalHourValues = {
      internalHourProccessed: kpiUtility.sanitize(noProcessed),
    };

    return kpiValues;
  },
);
